import * as React from 'react'
import { View, Text, TouchableOpacity, TextInput } from 'react-native'
import styled from '@theme/styled-components'

import TextInputComponent from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import KeyboardView from '@components/view/KeyboardView'

import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'
import * as I18nStore from '@i18n/i18n'
import { onNewUser } from '@store/store'
import useReducer from '@store/useReducer'

import api from '@api/api'

import useBreakpoints from '@utils/useBreakpoints'
import useI18n from '@i18n/useI18n'

import { useFormik } from 'formik'
import * as yup from 'yup'

interface Props {
  onBack: () => void
  onLoginSuccess?: () => void
  onLoginError?: (code?: number) => void
  onPressForgetPassword: () => void
}

interface Values {
  email: string
  password: string
}

const initialValues: Values = {
  email: '',
  password: '',
}

const validationSchema = yup.object().shape({
  email: yup.string(), //.email('errors.form.email').required('errors.form.empty'),
  password: yup.string(),
  // .min(8, 'errors.form.passwordTooShort')
  // .matches(/.*[0-9].*/, 'errors.form.passwordNumber') // at least 1 number
  // .matches(/.*[A-Z].*/, 'errors.form.passwordUpper') // at least 1 upper
  // .matches(/.*[a-z].*/, 'errors.form.passwordLower') // at least 1 lower
  // .matches(/.*[!"#$%&'()*+,-./:;<=>?@\[\]^_`{|}~\s»)].*/, 'errors.form.passwordSpecial') // at least 1 special
  // .required('errors.form.empty'),
})

const LoginForm = ({ onPressForgetPassword, onBack, onLoginSuccess, onLoginError }: Props) => {
  const i18n = useI18n()
  const [secured, setSecured] = React.useState(true)
  const [, bp] = useBreakpoints()

  const lastUser = useReducer(UserStore.store, (s) => s.lastUser)
  const lastUserEmail = useReducer(UserStore.store, (s) => s.lastUserEmail)

  const passwordInputRef = React.useRef<TextInput>(null)

  const onSubmit = (vals: Values) => {
    setSubmitting(true)

    const body: LoginRequest = {
      login: vals.email,
      password: vals.password,
    }

    api.authentification
      .login(body)
      .then((res) => {
        AuthStore.actions.setTokens({
          access: res.access_token,
          expireIn: res.expires_in,
          scopes: res.scopes,
          refresh: res.refresh_token,
        })

        AuthStore.actions.setScopes(res.scopes)
      })
      .then(api.authentification.me)
      .then((user) => {
        if (lastUser !== user.id) {
          // reset saved datas
          onNewUser()
        }

        if (user.langage) {
          // language to connected user
          I18nStore.actions.setLang(user.langage.toLowerCase() as Language)
        }

        // set user
        UserStore.actions.setUser(user)
      })
      .then(() => UserStore.actions.setLogged(true))
      .then(() => {
        if (onLoginSuccess) {
          onLoginSuccess()
        }
      })
      .then(api.incident.followed)
      .then(UserStore.actions.setFavoriteIncidents)
      .catch((err) => {
        if (onLoginError) {
          onLoginError(err.code)
        }
      })
      .finally(() => setSubmitting(false))
  }

  const { values, handleChange, handleBlur, errors, handleSubmit, isSubmitting, isValid, touched, setSubmitting } =
    useFormik<Values>({
      initialValues: { ...initialValues, email: lastUserEmail || '' },
      onSubmit,
      validationSchema,
      validateOnBlur: true,
      validateOnChange: true,
    })

  return (
    <MainContainer>
      <KeyboardView>
        {bp !== 'phone' && (
          <BackButton onPress={onBack}>
            <Icon name="long-arrow-left" size={40} color="black" />
          </BackButton>
        )}
        <Title>{i18n.t(`screens.login.form.title`)}</Title>
        <SubTitle>{i18n.t(`screens.login.form.subTitle`)}</SubTitle>
        <Inputs>
          <InputContainer>
            <TextInputComponent
              onChange={handleChange('email')}
              placeholder={i18n.t('screens.login.form.idPlaceholder')}
              right={{
                name:
                  touched.email && errors.email ? 'close' : isValid && values.password.length > 0 ? 'check' : 'profil',
              }}
              value={values.email}
              returnKeyType="next"
              error={touched.email && errors.email ? i18n.t(errors.email) : undefined}
              onBlur={handleBlur('email')}
              onSubmitEditing={() => passwordInputRef.current?.focus()}
              blurOnSubmit={false}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              keyboardType="email-address"
            />
          </InputContainer>

          <TextInputComponent
            inputRef={passwordInputRef}
            onChange={handleChange('password')}
            placeholder={i18n.t('screens.login.form.passwordPlaceholder')}
            right={{
              name: secured ? 'visibility' : 'unvisibility',
              onClick: () => setSecured(!secured),
            }}
            returnKeyType="send"
            secureTextEntry={secured}
            value={values.password}
            error={touched.password && errors.password ? i18n.t(errors.password) : undefined}
            onBlur={handleBlur('password')}
            onSubmitEditing={() => handleSubmit()}
          />
        </Inputs>
        <ForgotPassword onPress={onPressForgetPassword}>{i18n.t('screens.login.form.forgotPassword')}</ForgotPassword>
        <SubmitButton>
          <Button
            status={isSubmitting ? 'loading' : isValid && values.email.length > 0 ? 'active' : 'disabled'}
            label={i18n.t('screens.login.form.connexionButton')}
            onClick={handleSubmit}
          />
        </SubmitButton>
      </KeyboardView>
    </MainContainer>
  )
}

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `0px 16px 20px 16px` : '0px 48px')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1; justify-content: center;'}
`
const BackButton = styled(TouchableOpacity)`
  margin-bottom: 30px;
`
const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 48px;
  line-height: 56px;
  margin-bottom: 16px;
`
const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 28px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 48px;
  line-height: 32px;
`
const Inputs = styled(View)`
  margin-bottom: 8px;
`
const InputContainer = styled(View)`
  margin-bottom: 16px;
`
const SubmitButton = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 272px;`}
  margin-bottom: 20px;
`
const ForgotPassword = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.secondaryText};
  font-weight: 700;
  margin-bottom: 50px;
`
export default LoginForm
